import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatCurrency } from "@/lib/api";
import { Trash2, Plus } from "lucide-react";

const emptyItem = () => ({ description: "", quantity: 1, rate: 0 });

export default function CreateInvoice() {
  const navigate = useNavigate();
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [items, setItems] = useState([emptyItem()]);
  const [submitting, setSubmitting] = useState(false);

  const updateItem = (i, field, value) => {
    setItems((prev) =>
      prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it))
    );
  };
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (i) =>
    setItems((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));

  const subtotal = useMemo(
    () =>
      items.reduce(
        (s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0),
        0
      ),
    [items]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) {
      toast.error("Client name is required");
      return;
    }
    const cleanItems = items
      .filter((it) => it.description.trim() && Number(it.quantity) > 0)
      .map((it) => ({
        description: it.description.trim(),
        quantity: Number(it.quantity),
        rate: Number(it.rate),
      }));
    if (cleanItems.length === 0) {
      toast.error("Add at least one line item");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/invoices", {
        client_name: clientName.trim(),
        client_email: clientEmail.trim() || null,
        notes: notes.trim() || null,
        currency,
        line_items: cleanItems,
      });
      toast.success(`Invoice ${data.invoice_number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-12" data-testid="create-invoice-form">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          New Invoice
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-zinc-900 mt-2">
          Draft a bill.
        </h1>
      </div>

      <section className="grid md:grid-cols-2 gap-x-12 gap-y-8">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Bill to (client name)
          </label>
          <input
            data-testid="input-client-name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Acme Co."
            className="w-full border-0 border-b border-zinc-200 rounded-none px-0 py-3 focus:ring-0 focus:border-zinc-900 bg-transparent text-lg outline-none transition-colors placeholder:text-zinc-400 mt-1"
            required
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Client email (optional)
          </label>
          <input
            data-testid="input-client-email"
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="billing@acme.com"
            className="w-full border-0 border-b border-zinc-200 rounded-none px-0 py-3 focus:ring-0 focus:border-zinc-900 bg-transparent text-lg outline-none transition-colors placeholder:text-zinc-400 mt-1"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Currency
          </label>
          <select
            data-testid="input-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full border-0 border-b border-zinc-200 rounded-none px-0 py-3 focus:ring-0 focus:border-zinc-900 bg-transparent text-lg outline-none transition-colors mt-1"
          >
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — Pound</option>
            <option value="INR">INR — Rupee</option>
          </select>
        </div>
      </section>

      {/* Line items */}
      <section data-testid="line-items-section">
        <div className="flex justify-between items-baseline">
          <h2 className="font-display text-xl font-medium text-zinc-900">
            Line items
          </h2>
          <button
            type="button"
            data-testid="add-item-button"
            onClick={addItem}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-900 hover:text-zinc-600 transition-colors"
          >
            <Plus size={14} /> Add item
          </button>
        </div>

        <div className="mt-6 border-t border-zinc-900">
          <div className="hidden md:grid grid-cols-12 gap-3 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
            <div className="col-span-6">Description</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Rate</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-1" />
          </div>
          {items.map((it, idx) => {
            const amount = Number(it.quantity || 0) * Number(it.rate || 0);
            return (
              <div
                key={idx}
                data-testid={`line-item-${idx}`}
                className="grid grid-cols-12 gap-3 py-4 border-b border-zinc-200 items-center"
              >
                <input
                  data-testid={`item-${idx}-description`}
                  value={it.description}
                  onChange={(e) =>
                    updateItem(idx, "description", e.target.value)
                  }
                  placeholder="Design system audit"
                  className="col-span-12 md:col-span-6 border-0 border-b md:border-b-0 border-zinc-100 md:border-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-zinc-900 bg-transparent outline-none transition-colors placeholder:text-zinc-400"
                />
                <input
                  data-testid={`item-${idx}-quantity`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={it.quantity}
                  onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                  className="col-span-4 md:col-span-2 border-0 border-b md:border-b-0 border-zinc-100 md:border-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-zinc-900 bg-transparent outline-none transition-colors md:text-right"
                />
                <input
                  data-testid={`item-${idx}-rate`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={it.rate}
                  onChange={(e) => updateItem(idx, "rate", e.target.value)}
                  className="col-span-4 md:col-span-2 border-0 border-b md:border-b-0 border-zinc-100 md:border-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-zinc-900 bg-transparent outline-none transition-colors md:text-right"
                />
                <div
                  data-testid={`item-${idx}-amount`}
                  className="col-span-3 md:col-span-1 md:text-right text-zinc-900 font-medium"
                >
                  {formatCurrency(amount, currency)}
                </div>
                <button
                  type="button"
                  data-testid={`remove-item-${idx}`}
                  onClick={() => removeItem(idx)}
                  className="col-span-1 text-zinc-400 hover:text-zinc-900 transition-colors justify-self-end"
                  aria-label="Remove item"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div className="mt-8 flex justify-end">
          <div className="w-full md:w-80 space-y-3">
            <div className="flex justify-between text-zinc-500">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Subtotal
              </span>
              <span className="font-medium" data-testid="form-subtotal">
                {formatCurrency(subtotal, currency)}
              </span>
            </div>
            <div className="flex justify-between border-t border-zinc-900 pt-3">
              <span className="font-display text-lg font-medium">Total</span>
              <span
                className="font-display text-2xl font-medium"
                data-testid="form-total"
              >
                {formatCurrency(subtotal, currency)}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section>
        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
          Notes (optional)
        </label>
        <textarea
          data-testid="input-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Payment terms, thank-you note, etc."
          className="w-full border-0 border-b border-zinc-200 rounded-none px-0 py-3 focus:ring-0 focus:border-zinc-900 bg-transparent outline-none transition-colors placeholder:text-zinc-400 mt-1 resize-none"
        />
      </section>

      <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
        <button
          type="button"
          onClick={() => navigate("/")}
          data-testid="cancel-button"
          className="bg-transparent text-zinc-900 border border-zinc-200 rounded-none px-6 py-3 font-medium hover:border-zinc-900 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-invoice-button"
          className="bg-zinc-900 text-white rounded-none px-8 py-3 font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
        >
          {submitting ? "Creating..." : "Create invoice →"}
        </button>
      </div>
    </form>
  );
}
